import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Scale, FileText, Users, Shield, Brain, Clock, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import NavbarSimple from '../components/layout/NavbarSimple';
import FooterPro from '../components/layout/FooterPro';
import HeroCarousel from '../components/home/HeroCarousel';
import PromoBanners from '../components/home/PromoBanners';
import AboutSection from '../components/home/AboutSection';
import NewsSection from '../components/home/NewsSection';
import EventsSection from '../components/home/EventsSection';
import TwitterSection from '../components/home/TwitterSection';
import DivisionsSection from '../components/home/DivisionsSection';
import CustomerJourneySection from '../components/home/CustomerJourneySection';
import ClientLogosSection from '../components/home/ClientLogosSection';
import WazinWatermark from '../components/WazinWatermark';
import TestimonialsSectionEnhanced from '../components/TestimonialsSectionEnhanced';
import LocationsSection from '../components/LocationsSection';
import FAQSection from '../components/home/FAQSection';
import BackToTop from '../components/BackToTop';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const services = [
    {
      icon: Users,
      title: i18n.language === 'ar' ? '\u0627\u0644\u0627\u0633\u062a\u0634\u0627\u0631\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629' : 'Legal Consultations',
      description: i18n.language === 'ar'
        ? '\u0627\u0633\u062a\u0634\u0627\u0631\u0627\u062a \u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u062f\u0642\u064a\u0642\u0629 \u062a\u0633\u0627\u0639\u062f\u0643 \u0639\u0644\u0649 \u0641\u0647\u0645 \u0645\u0648\u0642\u0641\u0643 \u0627\u0644\u0646\u0638\u0627\u0645\u064a \u0648\u0627\u062a\u062e\u0627\u0630 \u0627\u0644\u0642\u0631\u0627\u0631 \u0628\u062b\u0642\u0629 \u0648\u0648\u0636\u0648\u062d.'
        : 'Precise legal consultations that help you understand your legal position and make decisions with confidence and clarity.',
      buttonText: i18n.language === 'ar' ? '\u0627\u062d\u062c\u0632 \u0627\u0633\u062a\u0634\u0627\u0631\u0629' : 'Book Consultation',
      path: '/book-consultation',
      accent: '#A27849',
    },
    {
      icon: FileText,
      title: i18n.language === 'ar' ? '\u0635\u064a\u0627\u063a\u0629 \u0648\u0645\u0631\u0627\u062c\u0639\u0629 \u0627\u0644\u0639\u0642\u0648\u062f' : 'Contract Drafting & Review',
      description: i18n.language === 'ar'
        ? '\u0639\u0642\u0648\u062f \u0645\u062d\u0643\u0645\u0629 \u062a\u064f\u0635\u0627\u063a \u0628\u0639\u0646\u0627\u064a\u0629 \u0644\u062d\u0645\u0627\u064a\u0629 \u0627\u0644\u062d\u0642\u0648\u0642\u060c \u0648\u062a\u0642\u0644\u064a\u0644 \u0627\u0644\u0645\u062e\u0627\u0637\u0631\u060c \u0648\u062a\u0646\u0638\u064a\u0645 \u0627\u0644\u0627\u0644\u062a\u0632\u0627\u0645\u0627\u062a \u0628\u0648\u0636\u0648\u062d.'
        : 'Well-structured contracts carefully drafted to protect rights, reduce risk, and define obligations clearly.',
      buttonText: i18n.language === 'ar' ? '\u0627\u0637\u0644\u0628 \u0627\u0644\u062e\u062f\u0645\u0629' : 'Request Service',
      path: '/services/contracts',
      accent: '#1F3B64',
    },
    {
      icon: Scale,
      title: i18n.language === 'ar' ? '\u0627\u0644\u062a\u0645\u062b\u064a\u0644 \u0627\u0644\u0642\u0636\u0627\u0626\u064a' : 'Litigation Representation',
      description: i18n.language === 'ar'
        ? '\u062a\u0645\u062b\u064a\u0644 \u0642\u0627\u0646\u0648\u0646\u064a \u0627\u062d\u062a\u0631\u0627\u0641\u064a \u0623\u0645\u0627\u0645 \u0627\u0644\u062c\u0647\u0627\u062a \u0627\u0644\u0642\u0636\u0627\u0626\u064a\u0629 \u0645\u0639 \u0645\u062a\u0627\u0628\u0639\u0629 \u062f\u0642\u064a\u0642\u0629 \u0644\u0643\u0644 \u0645\u0631\u062d\u0644\u0629 \u0645\u0646 \u0645\u0631\u0627\u062d\u0644 \u0627\u0644\u0642\u0636\u064a\u0629.'
        : 'Professional legal representation before judicial authorities with close follow-up at every stage of the case.',
      buttonText: i18n.language === 'ar' ? '\u0627\u0628\u062f\u0623 \u0637\u0644\u0628\u0643' : 'Start Your Request',
      path: '/services/litigation',
      accent: '#A27849',
    },
  ];
  const whyWazinItems = [
    {
      icon: Shield,
      title: i18n.language === 'ar' ? 'شريك قانوني... لا مجرد مقدم خدمة' : 'A Legal Partner, Not Just a Provider',
      description: i18n.language === 'ar'
        ? 'نعمل معك بفهم عميق لاحتياجك وبناء حلول متكاملة.'
        : 'We work with a deep understanding of your needs and build integrated solutions.',
      accent: '#A27849'
    },
    {
      icon: Brain,
      title: i18n.language === 'ar' ? 'معالجة قانونية دقيقة' : 'Precise Legal Handling',
      description: i18n.language === 'ar'
        ? 'تحليل قانوني مبني على الأنظمة والخبرة العملية.'
        : 'Legal analysis grounded in regulations and practical experience.',
      accent: '#1F3B64'
    },
    {
      icon: Clock,
      title: i18n.language === 'ar' ? 'سرعة في الإنجاز' : 'Speed in Delivery',
      description: i18n.language === 'ar'
        ? 'تنفيذ فعال مع الحفاظ على جودة المخرجات.'
        : 'Efficient execution while maintaining high-quality outcomes.',
      accent: '#A27849'
    },
    {
      icon: Award,
      title: i18n.language === 'ar' ? 'وضوح وشفافية' : 'Clarity and Transparency',
      description: i18n.language === 'ar'
        ? 'تواصل مستمر وفهم واضح لكل مرحلة من العمل.'
        : 'Consistent communication and clear understanding at every stage.',
      accent: '#1F3B64'
    },
    {
      icon: FileText,
      title: i18n.language === 'ar' ? 'صياغة قانونية احترافية' : 'Professional Legal Drafting',
      description: i18n.language === 'ar'
        ? 'وثائق ومستندات قانونية مُعدَّة بدقة تضمن الحماية وتُجنَّب النزاعات.'
        : 'Legal documents prepared with precision to ensure protection and prevent disputes.',
      accent: '#A27849'
    },
    {
      icon: CheckCircle2,
      title: i18n.language === 'ar' ? 'حضور رقمي منظم' : 'Organized Digital Presence',
      description: i18n.language === 'ar'
        ? 'منظومة رقمية تتيح للعميل متابعة ملفاته وطلباته بشفافية كاملة.'
        : 'A digital system enabling clients to track their files and requests with full transparency.',
      accent: '#1F3B64'
    }
  ];
  const features = [
    i18n.language === 'ar' ? 'منصة رقمية متكاملة' : 'Complete digital platform',
    i18n.language === 'ar' ? 'متابعة فورية للقضايا' : 'Real-time case tracking',
    i18n.language === 'ar' ? 'ذكاء اصطناعي متقدم' : 'Advanced AI technology',
    i18n.language === 'ar' ? 'إشعارات آلية' : 'Automated notifications',
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Wazin Watermark - Fixed Background */}
      <WazinWatermark opacity={0.12} size="large" position="center" />
      
      <div style={{ position: 'relative', zIndex: 100 }}>
        <NavbarSimple />
      </div>
      
      {/* Hero Carousel Section */}
      <HeroCarousel />

      {/* Promo Banners Grid - NEW */}
      <PromoBanners />

      {/* About Section - NEW */}
      <AboutSection />

      {/* News Section */}
      <NewsSection />

      {/* Events Section - NEW */}
      <EventsSection />

      {/* Twitter Feed Section - NEW */}
      <TwitterSection />

      {/* Hero Section - ظ†ظپط³ طھطµظ…ظٹظ… ط§ظ„ظ…ظˆظ‚ط¹ ط§ظ„ظ‚ط¯ظٹظ… */}
      <section className="relative pt-20 pb-20 px-4 overflow-hidden" style={{ background: '#1F2937' }} data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center fade-in">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white" data-testid="hero-title">
              {t('heroTitle')}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-4" data-testid="hero-subtitle">
              {t('heroSubtitle')}
              </p>
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              {t('heroDescription')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              <Button
                onClick={() => navigate('/book-consultation')}
                style={{ background: '#A27849' }}
                className="text-white hover:opacity-90 text-lg px-10 py-6 rounded-lg"
                data-testid="hero-book-consultation-btn"
              >
                {i18n.language === 'ar' ? '\u0627\u062d\u062c\u0632 \u0627\u0633\u062a\u0634\u0627\u0631\u0629' : 'Book Consultation'}
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-10 py-6 rounded-lg"
                data-testid="hero-learn-more-btn"
              >
                {t('learnMore')}
              </Button>
            </div>

            {/* Stats - ظ†ظپط³ طھطµظ…ظٹظ… ط§ظ„ظ…ظˆظ‚ط¹ ط§ظ„ظ‚ط¯ظٹظ… */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: '#A27849' }}>10+</p>
                <p className="text-gray-400 text-sm">{i18n.language === 'ar' ? '\u0633\u0646\u0629 \u062e\u0628\u0631\u0629' : 'Years Experience'}</p>
              </div>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: '#A27849' }}>300+</p>
                <p className="text-gray-400 text-sm">{i18n.language === 'ar' ? '\u0639\u0645\u064a\u0644 \u0633\u0639\u064a\u062f' : 'Clients'}</p>
              </div>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: '#A27849' }}>270+</p>
                <p className="text-gray-400 text-sm">{i18n.language === 'ar' ? '\u0642\u0636\u064a\u0629 \u0646\u0627\u062c\u062d\u0629' : 'Successful Cases'}</p>
              </div>
              <div className="text-center">
                <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: '#A27849' }}>10+</p>
                <p className="text-gray-400 text-sm">{i18n.language === 'ar' ? '\u062e\u0628\u064a\u0631 \u0642\u0627\u0646\u0648\u0646\u064a' : 'Experts'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Refined Three-Card Layout */}
      <section id="services" className="py-20 px-4 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: '#1F2937' }}>
              {i18n.language === 'ar' ? '\u062d\u0644\u0648\u0644 \u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0623\u0633\u0627\u0633\u064a\u0629 \u062a\u0628\u062f\u0623 \u0645\u0646\u0647\u0627 \u0631\u062d\u0644\u062a\u0643' : 'Core Legal Solutions to Begin Your Journey'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              {i18n.language === 'ar'
                ? '\u0646\u0642\u062f\u0651\u0645 \u0644\u0643 \u0623\u0628\u0631\u0632 \u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0627\u0644\u062a\u064a \u062a\u0634\u0643\u0651\u0644 \u0646\u0642\u0637\u0629 \u0627\u0644\u0628\u062f\u0627\u064a\u0629 \u0644\u0627\u062a\u062e\u0627\u0630 \u0627\u0644\u0642\u0631\u0627\u0631 \u0627\u0644\u0635\u062d\u064a\u062d\u060c \u0648\u0628\u0646\u0627\u0621 \u0627\u0644\u062d\u0645\u0627\u064a\u0629 \u0627\u0644\u0646\u0638\u0627\u0645\u064a\u0629 \u0627\u0644\u0645\u0646\u0627\u0633\u0628\u0629 \u0644\u0627\u062d\u062a\u064a\u0627\u062c\u0643.'
                : 'We provide the key legal services that form the right starting point for informed decisions and the appropriate legal protection for your needs.'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 fade-in border border-gray-200 overflow-hidden hover:-translate-y-1"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`service-card-${index}`}
              >
                <div className="h-2 w-full" style={{ background: service.accent }} />
                <div className="p-8" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <div
                    className="w-[72px] h-[72px] rounded-2xl flex items-center justify-center mb-6 shadow-sm group-hover:scale-105 transition-transform duration-300"
                    style={{ background: service.accent }}
                  >
                    <service.icon className="w-9 h-9 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 leading-snug" style={{ color: '#1F2937' }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-8 min-h-[96px] mb-8 text-base">
                    {service.description}
                  </p>
                  <Button
                    onClick={() => navigate(service.path)}
                    className="w-full md:w-auto px-7 py-6 text-base font-bold text-white hover:opacity-95 transition-all gap-2 rounded-xl"
                    style={{ background: service.accent }}
                  >
                    {service.buttonText}
                    <ArrowRight className={`w-5 h-5 ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {/* View All Services Button */}
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/services')}
              className="bg-gradient-to-r from-wazin-gold to-wazin-gold-600 text-white px-8 py-6 text-lg hover:shadow-lg transition-all gap-2 font-bold"
            >
              {i18n.language === 'ar' ? '\u0639\u0631\u0636 \u062c\u0645\u064a\u0639 \u0627\u0644\u062e\u062f\u0645\u0627\u062a' : 'View All Services'}
              <ArrowRight className={`w-5 h-5 ${i18n.language === 'ar' ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>
      </section>

      {/* Why Wazin Section */}
      <section id="why-wazin" className="py-20 px-4 bg-white relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-wazin-navy">
              {i18n.language === 'ar' ? 'لماذا وازن؟' : 'Why Wazin?'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {i18n.language === 'ar'
                ? 'نقدّم تجربة قانونية تعتمد على الدقة، والوضوح، وبناء الحلول العملية.'
                : 'We deliver a legal experience built on precision, clarity, and practical solutions.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {whyWazinItems.map((item, index) => {
              const Icon = item.icon;

              return (
                <Card
                  key={index}
                  className="group border border-[#A27849]/15 bg-white shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                >
                  <div className="h-1.5 w-full bg-gradient-to-r from-[#B58C5A] via-[#A27849] to-[#8C653A]" />
                  <CardContent className="p-8">
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mb-6 shadow-sm group-hover:scale-105 transition-transform duration-300"
                      style={{ background: item.accent }}
                    >
                      <Icon className="w-7 h-7 text-white" />
                    </div>

                    <h3 className="text-2xl font-bold text-wazin-navy mb-4 leading-snug group-hover:text-[#8C653A] transition-colors duration-300">
                      {item.title}
                    </h3>

                    <p className="text-gray-600 leading-8 text-base">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <ClientLogosSection />

      {/* Customer Journey Section */}
      <CustomerJourneySection />

      {/* Legal Divisions Section */}
      <DivisionsSection />

      {/* Testimonials Section - ط¢ط±ط§ط، ط§ظ„ط¹ظ…ظ„ط§ط، */}
      <TestimonialsSectionEnhanced />

      {/* Locations Section - ظ…ظˆط§ظ‚ط¹ظ†ط§ */}
      <LocationsSection />

      {/* FAQ Section */}
      <FAQSection />

      <FooterPro />

      {/* Back to Top Button */}
      <BackToTop />
      
    </div>
  );
};

export default HomePage;

